import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/services/api';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      setSent(true);
    } catch (error) {
      // Backend intentionally returns 200 even for unknown emails for security,
      // so any error here is network/server.
      if (error.message === 'Network Error' || !error.response) {
        toast.error("Couldn't reach the server — please check your connection.");
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send reset link');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200" data-testid="forgot-password-page">
        <CardHeader>
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
            data-testid="forgot-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
          <CardTitle className="text-2xl text-slate-900">
            {sent ? 'Check your email' : 'Reset your password'}
          </CardTitle>
          <CardDescription>
            {sent
              ? `If an account exists for ${email}, we've sent a password reset link.`
              : "Enter your email and we'll send you a link to reset your password."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-600">
                Didn't receive it? Check your spam folder, or
                {' '}
                <button
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="text-teal-600 hover:underline"
                  data-testid="forgot-resend-btn"
                >
                  try a different email
                </button>.
              </p>
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700">
                  Return to login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send reset link
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
